<template>
  <div data-testid="formas-pagamento-table">
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :adjustColumnsWidth="adjustColumnsWidthConfig"
      :pdfButton="pdfButtonConfig"
      :csvButton="csvButtonConfig"
      :clipboardButton="clipboardButtonConfig"
      :printButton="printButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @row-click="handleRowClick"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';
import DataTable from '../../grid/DataTable.vue';

type FormaPagamentoId = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  data?: Record<string, unknown>;
  value?: unknown;
  node?: { rowPinned?: boolean | string };
}

withDefaults(defineProps<{
  usuarioVersao?: number;
}>(), {
  usuarioVersao: 0
});

const emit = defineEmits<{
  edit: [formaPagamentoId: FormaPagamentoId];
}>();

// Get i18n composable
const { t, n } = useI18n();
const { initialized } = useFiltro();

// Reactive data
const datatableRef = ref<DataTableInstance | null>(null);

// Computed properties
const endpoint = computed<string>(() => '/index.php/config/formas_pagamento/lista');

// Helper function to get translated possui_taxas label
const getPossuiTaxasLabel = (value: unknown): string => {
  const labels: Record<string, string> = {
    '0': t('Não'),
    '1': t('Sim - mensal'),
    '2': t('Sim - por transação'),
    '3': t('Sim - mensal e por transação')
  };
  return labels[String(value)] || String(value ?? '');
};

// Helper function to get translated tipo label
const getTipoTaxaLabel = (value: unknown): string => {
  const labels: Record<string, string> = {
    '1': t('Percentual'),
    '2': t('Fixo por transação')
  };
  return labels[String(value)] || String(value ?? '');
};

// Helper function to get translated aceita_parcelado label
const getAceitaParceladoLabel = (value: unknown): string => {
  return value == 1 ? t('Sim') : t('Não');
};

// Helper function to get translated codigo label
const getCodigoLabel = (value: unknown): string => {
  const labels: Record<string, string> = {
    'CASH': t('Dinheiro'),
    'PIX': t('PIX'),
    'CREDIT_CARD': t('Cartão de Crédito'),
    'DEBIT_CARD': t('Cartão de Débito'),
    'BANK_TRANSFER': t('Transferência Bancária'),
    'BOLETO': t('Boleto'),
    'REWORK': t('Retrabalho'),
    'BARTER': t('Permuta'),
    'CHECK': t('Cheque')
  };
  const key = String(value ?? '');
  return labels[key] || key || '-';
};

const tableColumns = computed(() => {
  return [
    {
      data: 'forma_pagamento_id',
      title: t('ID'),
      width: 80,
      resizable: true,
      sortable: true,
      renderType: 'clickable_id'
    },
    {
      data: 'forma_pagamento_nome',
      title: t('Nome da forma de pagamento'),
      width: 200,
      resizable: true,
      sortable: true
    },
    {
      data: 'forma_pagamento_codigo',
      title: t('Código'),
      width: 150,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams) => {
        // For pinned summary row, show raw value
        if (params.node && params.node.rowPinned) {
          return params.value != null ? params.value : '';
        }
        return getCodigoLabel(params.value);
      }
    },
    {
      data: 'forma_pagamento_possui_taxas',
      title: t('Possui taxas?'),
      width: 200,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams) => {
        // For pinned summary row, show raw value
        if (params.node && params.node.rowPinned) {
          return params.value != null ? params.value : '';
        }
        return getPossuiTaxasLabel(params.value);
      }
    },
    {
      data: 'forma_pagamento_aceita_parcelado',
      title: t('Aceita parcelar?'),
      width: 150,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams) => {
        // For pinned summary row, show raw value
        if (params.node && params.node.rowPinned) {
          return params.value != null ? params.value : '';
        }
        return getAceitaParceladoLabel(params.value);
      }
    },
    {
      data: 'forma_pagamento_taxa_mensal',
      title: t('Taxa mensal'),
      width: 120,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams) => {
        // For pinned summary row, show raw value
        if (params.node && params.node.rowPinned) {
          return params.value != null ? params.value : '';
        }
        const value = parseFloat(String(params.value ?? '')) || 0;
        return n(value, 'percent');
      }
    },
    {
      data: 'forma_pagamento_taxa_transacao',
      title: t('Taxa por transação'),
      width: 150,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams) => {
        // For pinned summary row, show raw value
        if (params.node && params.node.rowPinned) {
          return params.value != null ? params.value : '';
        }

        const value = parseFloat(String(params.value ?? '')) || 0;
        const tipo = params.data?.forma_pagamento_taxa_transacao_tipo;

        if (tipo == 1) {
          // Percentage
          return n(value, 'percent');
        } else if (tipo == 2) {
          // Fixed
          return n(value, 'currency');
        }
        return n(value);
      }
    },
    {
      data: 'forma_pagamento_taxa_transacao_tipo',
      title: t('Tipo da taxa de transação'),
      width: 180,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams) => {
        // For pinned summary row, show raw value
        if (params.node && params.node.rowPinned) {
          return params.value != null ? params.value : '';
        }
        return getTipoTaxaLabel(params.value);
      }
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    tableId: 'tabelaFormasPagamento',
    title: t('Relatório de Formas de Pagamento'),
    filename: `formas_pagamento_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 25,
    height: '500px',
    footer: false,
    rowSelection: 'single',
    rowTestId: (data: Record<string, unknown>) => {
      const id = data?.forma_pagamento_id;
      return id == null ? '' : `formas-pagamento-row-${id}`;
    }
  };
});

const filterConfig = computed(() => {
  return {
    searchName: t('Procurar'),
    choosePlaceholder: t('Escolha'),
    refreshName: t('Atualizar'),
    printName: t('Imprimir'),
    debug: false,
    columns: {
      'forma_pagamento_id': 'text',
      'forma_pagamento_nome': 'text',
      'forma_pagamento_codigo': 'select',
      'forma_pagamento_possui_taxas': 'select',
      'forma_pagamento_aceita_parcelado': 'select',
      'forma_pagamento_taxa_mensal': 'range',
      'forma_pagamento_taxa_transacao': 'range',
      'forma_pagamento_taxa_transacao_tipo': 'select'
    }
  };
});

const pdfButtonConfig = computed(() => {
  return {
    show: true,
    text: t('PDF'),
    loading: false
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV'),
    loading: false
  };
});

const clipboardButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Copiar'),
    loading: false
  };
});

const printButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Imprimir'),
    loading: false
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const adjustColumnsWidthConfig = computed(() => {
  return {
    show: true,
    text: t('Ajustar Colunas'),
    title: t('Ajustar largura das colunas')
  };
});

// Methods
const loadData = (): void => {
  const stack = new Error().stack;
  log('FormasPagamentoTable', 'loadData', 'Triggering grid refresh', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('FormasPagamentoTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t('Carregados') + ` ${data.length} ` + t('registros de formas de pagamento'));
};

const handleDataError = (error: Error): void => {
  log('FormasPagamentoTable', 'handleDataError', 'Error loading data', error);
  eventBus.emit('globalMessage.error', t('Erro ao carregar formas de pagamento: ') + error.message);
};

const editItem = (formaPagamentoId: FormaPagamentoId): void => {
  log('FormasPagamentoTable', 'editItem', 'Editing ID', formaPagamentoId);
  // Emit event to parent component to handle editing
  emit('edit', formaPagamentoId);
};

const handleRowClick = (rowData?: { forma_pagamento_id?: FormaPagamentoId } | null): void => {
  log('FormasPagamentoTable', 'handleRowClick', 'Row clicked', rowData?.forma_pagamento_id || null);
  // Handle row click if needed
};

const handleEditClick = (formaPagamentoId: FormaPagamentoId): void => {
  log('FormasPagamentoTable', 'handleEditClick', 'Edit clicked for ID', formaPagamentoId);
  editItem(formaPagamentoId);
};

const handleFilterUpdated = (): void => {
  log('FormasPagamentoTable', 'event:filter.updated', 'Received');
  loadData();
};

const handleFormaSaved = (): void => {
  log('FormasPagamentoTable', 'event:formas-pagamento.saved', 'Received');
  loadData();
};

const handleFormaDeleted = (): void => {
  log('FormasPagamentoTable', 'event:formas-pagamento.deleted', 'Received');
  loadData();
};

// Lifecycle
onMounted(() => {
  log('FormasPagamentoTable', 'mounted', 'Setting up event listeners', {
    filterInitialized: initialized.value
  });

  // Don't load data on mount - let DataTable handle initial load via autoLoad
  log('FormasPagamentoTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  // Listen for filter updates
  eventBus.on('filter.updated', handleFilterUpdated);
  eventBus.on('formas-pagamento.saved', handleFormaSaved);
  eventBus.on('formas-pagamento.deleted', handleFormaDeleted);
});

onBeforeUnmount(() => {
  log('FormasPagamentoTable', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up event listeners
  eventBus.off('filter.updated', handleFilterUpdated);
  eventBus.off('formas-pagamento.saved', handleFormaSaved);
  eventBus.off('formas-pagamento.deleted', handleFormaDeleted);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
.text-center {
  text-align: center;
}

</style>
